/**
 * Token Configuration for TPHILE
 * Contains network-specific token addresses and metadata
 */

// Network configurations
export const NETWORKS = {
    // Polygon Amoy Testnet (Deprecated - kept for reference)
    POLYGON_AMOY: {
        chainId: 80002,
        chainIdHex: '0x13882',
        name: 'Polygon Amoy Testnet',
        shortName: 'Amoy',
        rpcUrl: 'https://rpc-amoy.polygon.technology/',
        blockExplorer: 'https://amoy.polygonscan.com',
        nativeCurrency: {
            name: 'POL',
            symbol: 'POL',
            decimals: 18
        },
        tokenAddress: '0xb18e1b9f6Db78D9c492328e009a59d4227c5d1FC' // Old testnet address
    },
    // Polygon Mainnet (Production network)
    POLYGON_MAINNET: {
        chainId: 137,
        chainIdHex: '0x89',
        name: 'Polygon Mainnet',
        shortName: 'Polygon',
        rpcUrl: 'https://polygon-rpc.com',
        blockExplorer: 'https://polygonscan.com',
        nativeCurrency: {
            name: 'POL',
            symbol: 'POL',
            decimals: 18
        },
        tokenAddress: process.env.REACT_APP_TPHILE_TOKEN_CONTRACT || '0x9A77152343981490c6477aF75A7c3FCA28cf9f00'
    }
};

// Current active network
export const ACTIVE_NETWORK = NETWORKS.POLYGON_MAINNET;

// Token metadata
export const TOKEN_METADATA = {
    symbol: 'TPH',
    name: 'TPhile Token',
    decimals: 18,
    image: 'https://trendphile.com/favicon.ico',
    // Checksummed address (will be set dynamically based on network)
    getAddress: (network = ACTIVE_NETWORK) => network.tokenAddress
};

// Helper to get checksummed address
export const getChecksummedAddress = (address) => {
    if (!address) return null;
    // Simple checksum - for production use ethers.getAddress()
    return address;
};

// Helper to check if on correct network
export const isCorrectNetwork = async () => {
    if (!window.ethereum) return false;
    
    try {
        const chainIdHex = await window.ethereum.request({ method: 'eth_chainId' });
        const chainId = parseInt(chainIdHex, 16);
        return chainId === ACTIVE_NETWORK.chainId;
    } catch (error) {
        console.error('Error checking network:', error);
        return false;
    }
};

// Helper to get current network info
export const getCurrentNetwork = async () => {
    if (!window.ethereum) return null;
    
    try {
        const chainIdHex = await window.ethereum.request({ method: 'eth_chainId' });
        const chainId = parseInt(chainIdHex, 16);
        
        // Find matching network
        const network = Object.values(NETWORKS).find(n => n.chainId === chainId);
        
        return {
            chainId,
            chainIdHex,
            name: network?.name || `Unknown Network (${chainId})`,
            isSupported: !!network?.tokenAddress
        };
    } catch (error) {
        console.error('Error getting network:', error);
        return null;
    }
};

// Helper to switch to correct network
export const switchToNetwork = async (targetNetwork = ACTIVE_NETWORK) => {
    if (!window.ethereum) {
        throw new Error('MetaMask not available');
    }

    try {
        // Try to switch to the network
        await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: targetNetwork.chainIdHex }],
        });
        return true;
    } catch (switchError) {
        // Network not added to MetaMask - add it
        if (switchError.code === 4902) {
            try {
                await window.ethereum.request({
                    method: 'wallet_addEthereumChain',
                    params: [{
                        chainId: targetNetwork.chainIdHex,
                        chainName: targetNetwork.name,
                        nativeCurrency: targetNetwork.nativeCurrency,
                        rpcUrls: [targetNetwork.rpcUrl],
                        blockExplorerUrls: [targetNetwork.blockExplorer]
                    }]
                });
                return true;
            } catch (addError) {
                console.error('Error adding network:', addError);
                throw new Error(`Failed to add ${targetNetwork.name} to MetaMask`);
            }
        }
        throw switchError;
    }
};

export default {
    NETWORKS,
    ACTIVE_NETWORK,
    TOKEN_METADATA,
    getChecksummedAddress,
    isCorrectNetwork,
    getCurrentNetwork,
    switchToNetwork
};


